(() => {
  'use strict';

  const config = window.SRTheme || {};
  const routes = config.routes || {};
  const strings = config.strings || {};
  const body = document.body;

  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));
  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#039;', '"': '&quot;' })[character]);

  const announcement = qs('[data-announcement]');
  if (announcement) {
    const messages = qsa('[data-announcement-message]', announcement);
    const speed = Math.max(3000, Number(announcement.dataset.speed || 5000));
    if (messages.length > 1) {
      let index = 0;
      window.setInterval(() => {
        messages[index].classList.remove('is-active');
        index = (index + 1) % messages.length;
        messages[index].classList.add('is-active');
      }, speed);
    }
  }

  const formatMoney = cents => {
    const value = Number(cents || 0);
    const format = config.moneyFormat || '${{amount}}';
    const amount = (value / 100).toFixed(2);
    return format
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(value / 100).toLocaleString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
      .replace(/\{\{\s*amount\s*\}\}/, Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  };

  const toast = message => {
    const element = qs('.toast');
    if (!element || !message) return;
    element.textContent = message;
    element.hidden = false;
    window.clearTimeout(element._timer);
    element._timer = window.setTimeout(() => { element.hidden = true; }, 2800);
  };

  const setLocked = locked => body.classList.toggle('is-locked', locked);

  const mobileToggle = qs('[data-mobile-menu-toggle]');
  const mobileNav = qs('[data-mobile-nav]');
  const setMobileMenu = open => {
    if (!mobileToggle || !mobileNav) return;
    mobileToggle.setAttribute('aria-expanded', String(open));
    mobileNav.hidden = !open;
    const openIcon = qs('[data-menu-open-icon]', mobileToggle);
    const closeIcon = qs('[data-menu-close-icon]', mobileToggle);
    if (openIcon) openIcon.hidden = open;
    if (closeIcon) closeIcon.hidden = !open;
  };
  mobileToggle?.addEventListener('click', () => setMobileMenu(mobileNav.hidden));

  const searchPanel = qs('[data-search-panel]');
  const setSearch = open => {
    if (!searchPanel) return;
    searchPanel.hidden = !open;
    if (open) window.setTimeout(() => qs('[data-search-input]', searchPanel)?.focus(), 50);
  };
  qsa('[data-search-toggle]').forEach(button => button.addEventListener('click', () => setSearch(searchPanel?.hidden !== false)));
  qsa('[data-search-close]').forEach(button => button.addEventListener('click', () => setSearch(false)));

  const cartShell = qs('[data-cart-shell]');
  const cartDrawer = qs('[data-cart-drawer]');
  const setCartDrawer = open => {
    if (!cartShell || !config.cartDrawer) return;
    cartShell.hidden = !open;
    setLocked(open);
    if (open) window.setTimeout(() => cartDrawer?.focus(), 20);
  };
  qsa('[data-cart-open]').forEach(trigger => trigger.addEventListener('click', event => {
    if (!config.cartDrawer) return;
    event.preventDefault();
    setCartDrawer(true);
  }));
  qsa('[data-cart-close]').forEach(trigger => trigger.addEventListener('click', () => setCartDrawer(false)));

  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    setMobileMenu(false);
    setSearch(false);
    setCartDrawer(false);
    qsa('[data-media-lightbox]').forEach(lightbox => { lightbox.hidden = true; });
    setLocked(false);
  });

  const renderCart = cart => {
    qsa('[data-cart-count]').forEach(counter => {
      counter.textContent = cart.item_count;
      counter.hidden = cart.item_count === 0;
    });

    const bodyElement = qs('[data-cart-body]');
    const footer = qs('[data-cart-footer]');
    if (!bodyElement || !footer) return;

    if (!cart.item_count) {
      bodyElement.innerHTML = `
        <div class="cart-empty">
          <span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 8h14l-1 13H6L5 8Z"/><path d="M9 9V6a3 3 0 0 1 6 0v3"/></svg></span>
          <h3>${escapeHtml(strings.emptyCart || 'Your cart is empty')}</h3>
          <p>${escapeHtml(strings.emptyCartNote || 'Add a fresh pack to begin.')}</p>
          <a class="button button--accent" href="/collections/all">${escapeHtml(strings.shopNow || 'Shop now')}</a>
        </div>`;
      footer.hidden = true;
      return;
    }

    bodyElement.innerHTML = `<div class="cart-drawer__items">${cart.items.map((item, index) => `
      <div class="cart-line" data-cart-line data-line="${index + 1}">
        <a class="cart-line__image" href="${escapeHtml(item.url)}">${item.image ? `<img src="${escapeHtml(item.image)}${item.image.includes('?') ? '&' : '?'}width=180" width="180" height="180" loading="lazy" alt="${escapeHtml(item.product_title)}">` : ''}</a>
        <div class="cart-line__content">
          <a href="${escapeHtml(item.url)}"><strong>${escapeHtml(item.product_title)}</strong></a>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<small>${escapeHtml(item.variant_title)}</small>` : ''}
          <span>${formatMoney(item.final_line_price)}</span>
          <button type="button" data-cart-remove data-line="${index + 1}">${escapeHtml(strings.remove || 'Remove')}</button>
        </div>
      </div>`).join('')}</div>`;
    const subtotal = qs('[data-cart-subtotal]', footer);
    if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
    footer.hidden = false;
  };

  const getCart = async () => {
    const response = await fetch(`${routes.cart || '/cart'}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to load cart');
    const cart = await response.json();
    renderCart(cart);
    return cart;
  };

  const addToCart = async form => {
    const button = qs('[type="submit"]', form);
    const label = qs('[data-add-label], button span', form);
    const original = label?.textContent;
    if (button) button.disabled = true;
    if (label) label.textContent = strings.adding || 'Adding…';
    try {
      const response = await fetch(`${routes.cartAdd || '/cart/add'}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      if (!response.ok) throw new Error((await response.json()).description || strings.cartError);
      await getCart();
      toast(strings.added || 'Added to cart');
      if (config.cartDrawer) setCartDrawer(true);
    } catch (error) {
      toast(error.message || strings.cartError || 'Something went wrong.');
    } finally {
      if (button) button.disabled = false;
      if (label) label.textContent = original || strings.add || 'Add to cart';
    }
  };

  qsa('[data-ajax-product-form]').forEach(form => form.addEventListener('submit', event => {
    event.preventDefault();
    addToCart(form);
  }));

  cartShell?.addEventListener('click', async event => {
    const removeButton = event.target.closest('[data-cart-remove]');
    if (!removeButton) return;
    removeButton.disabled = true;
    try {
      const response = await fetch(`${routes.cartChange || '/cart/change'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(removeButton.dataset.line), quantity: 0 })
      });
      if (!response.ok) throw new Error(strings.cartError);
      renderCart(await response.json());
    } catch (error) {
      toast(error.message || strings.cartError || 'Something went wrong.');
      removeButton.disabled = false;
    }
  });

  qsa('[data-quantity-picker]').forEach(picker => {
    const input = qs('input[type="number"]', picker);
    qs('[data-quantity-minus]', picker)?.addEventListener('click', () => { input.value = Math.max(Number(input.min || 1), Number(input.value || 1) - 1); });
    qs('[data-quantity-plus]', picker)?.addEventListener('click', () => { input.value = Number(input.value || 1) + 1; });
  });

  qsa('[data-product-root]').forEach(root => {
    const select = qs('[data-variant-select]', root);
    const idInput = qs('[data-variant-id]', root);
    const price = qs('[data-product-price]', root);
    const compare = qs('[data-product-compare]', root);
    const save = qs('[data-product-save]', root);
    const button = qs('[data-add-to-cart]', root);
    const label = qs('[data-add-label]', root);
    const inventoryStatus = qs('[data-inventory-status]', root);
    const inventoryLabel = qs('[data-inventory-label]', root);

    const updatePackPrices = variantPrice => {
      qsa('[data-bundle-qty]', root).forEach(pack => {
        const packPrice = qs('[data-bundle-price]', pack);
        if (packPrice) packPrice.textContent = formatMoney(variantPrice * Number(pack.dataset.bundleQty || 1));
      });
    };

    const updateInventory = option => {
      if (!inventoryStatus || !inventoryLabel || !option) return;
      const tracked = option.dataset.inventoryTracked === 'true';
      const inventory = Number(option.dataset.inventory || 0);
      const threshold = Number(inventoryStatus.dataset.threshold || 8);
      const low = tracked && inventory > 0 && inventory <= threshold;
      inventoryStatus.classList.toggle('is-low', low);
      inventoryLabel.textContent = low ? (strings.lowStock || 'Only __count__ packs left').replace('__count__', inventory) : (strings.inventoryAvailable || 'Fresh batch available');
    };

    const selectMedia = mediaId => {
      if (!mediaId) return;
      const thumbnail = qs(`[data-media-thumbnail][data-media-id="${mediaId}"]`, root);
      thumbnail?.click();
    };

    select?.addEventListener('change', () => {
      const option = select.selectedOptions[0];
      const available = option.dataset.available === 'true';
      const variantPrice = Number(option.dataset.price || 0);
      const variantCompare = Number(option.dataset.compare || 0);
      if (idInput) idInput.value = option.value;
      if (price) price.textContent = formatMoney(variantPrice);
      if (compare) {
        compare.hidden = variantCompare <= variantPrice;
        compare.textContent = variantCompare > variantPrice ? formatMoney(variantCompare) : '';
      }
      if (save) {
        save.hidden = variantCompare <= variantPrice;
        save.textContent = variantCompare > variantPrice ? `${strings.save || 'Save'} ${formatMoney(variantCompare - variantPrice)}` : '';
      }
      if (button) button.disabled = !available;
      if (label) label.textContent = available ? (strings.add || 'Add to cart') : (strings.soldOut || 'Sold out');
      updatePackPrices(variantPrice);
      updateInventory(option);
      qsa('[data-sticky-price]').forEach(stickyPrice => { stickyPrice.textContent = formatMoney(variantPrice); });
      selectMedia(option.dataset.featuredMedia);
    });

    qsa('[data-bundle-qty]', root).forEach(pack => pack.addEventListener('click', () => {
      qsa('[data-bundle-qty]', root).forEach(item => item.classList.toggle('is-active', item === pack));
      const quantity = qs('input[name="quantity"]', root);
      if (quantity) quantity.value = Number(pack.dataset.bundleQty || 1);
    }));
  });

  qsa('[data-product-gallery]').forEach(gallery => {
    const thumbnails = qsa('[data-media-thumbnail]', gallery);
    const activateThumbnail = thumbnail => {
      const mediaId = thumbnail.dataset.mediaId;
      thumbnails.forEach(item => item.classList.toggle('is-active', item === thumbnail));
      qsa('[data-media-panel]', gallery).forEach(panel => {
        const active = panel.dataset.mediaId === mediaId;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
    };
    thumbnails.forEach(thumbnail => thumbnail.addEventListener('click', () => activateThumbnail(thumbnail)));
    const moveGallery = direction => {
      if (!thumbnails.length) return;
      const activeIndex = Math.max(0, thumbnails.findIndex(item => item.classList.contains('is-active')));
      activateThumbnail(thumbnails[(activeIndex + direction + thumbnails.length) % thumbnails.length]);
    };
    qs('[data-gallery-prev]', gallery)?.addEventListener('click', () => moveGallery(-1));
    qs('[data-gallery-next]', gallery)?.addEventListener('click', () => moveGallery(1));

    qs('[data-product-zoom]', gallery)?.addEventListener('click', () => {
      const activeImage = qs('[data-media-panel]:not([hidden]) img', gallery);
      const lightbox = gallery.closest('[data-product-root]')?.nextElementSibling;
      const lightboxImage = lightbox?.matches('[data-media-lightbox]') ? qs('[data-lightbox-image]', lightbox) : null;
      if (!activeImage || !lightbox || !lightboxImage) return;
      lightboxImage.src = activeImage.currentSrc || activeImage.src;
      lightboxImage.alt = activeImage.alt || '';
      lightbox.hidden = false;
      setLocked(true);
    });
  });

  qsa('[data-media-lightbox]').forEach(lightbox => qsa('[data-lightbox-close]', lightbox).forEach(button => button.addEventListener('click', () => {
    lightbox.hidden = true;
    setLocked(false);
  })));

  qsa('[data-sale-countdown]').forEach(countdown => {
    const end = new Date(countdown.dataset.end).getTime();
    if (!Number.isFinite(end)) { countdown.hidden = true; return; }
    const update = () => {
      const remaining = end - Date.now();
      if (remaining <= 0) { countdown.hidden = true; return false; }
      const days = Math.floor(remaining / 86400000);
      const hours = Math.floor((remaining % 86400000) / 3600000);
      const minutes = Math.floor((remaining % 3600000) / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      const set = (selector, value) => { const element = qs(selector, countdown); if (element) element.textContent = String(value).padStart(2, '0'); };
      set('[data-countdown-days]', days); set('[data-countdown-hours]', hours); set('[data-countdown-minutes]', minutes); set('[data-countdown-seconds]', seconds);
      return true;
    };
    if (update()) {
      const timer = window.setInterval(() => { if (!update()) window.clearInterval(timer); }, 1000);
    }
  });

  qsa('[data-sticky-add]').forEach(button => button.addEventListener('click', () => {
    const form = document.getElementById(button.dataset.formId);
    form?.requestSubmit();
  }));

  const stickyProduct = qs('[data-sticky-product]');
  const primaryAddButton = qs('.product-section [data-add-to-cart]');
  if (stickyProduct && primaryAddButton && 'IntersectionObserver' in window) {
    const stickyObserver = new IntersectionObserver(entries => {
      stickyProduct.hidden = entries[0].isIntersecting || window.scrollY < 280;
    }, { threshold: 0.2 });
    stickyObserver.observe(primaryAddButton);
  }

  if ('IntersectionObserver' in window && body.classList.contains('reveal-enabled')) {
    const items = qsa('[data-reveal]');
    items.forEach(item => item.classList.add('reveal-pending'));
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      entry.target.classList.remove('reveal-pending');
      observer.unobserve(entry.target);
    }), { rootMargin: '0px 0px -40px', threshold: 0.08 });
    items.forEach(item => observer.observe(item));
  }
})();
